<script setup>
import chroma from 'chroma-js';
import BiColorRangePicker from '~/bi/components/chart-builder/common/bi-color-range-picker.vue';
import { useTableConditionalFormatter } from '~/bi/components/chart-builder/tabs/table-tabs/conditional-formatting/composables/bi-conditional-formatter.composable.js';
import { BI_DEFAULT_PALETTE_COLORS } from '~/bi/constants/bi-constants';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  fields: {
    type: Array,
    required: true,
  },
  fieldsMap: {
    type: Object,
    default: () => ({}),
  },
  ruleDetails: {
    type: Object,
    default: () => ({}),
  },
  chartType: {
    type: String,
    required: true,
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['ruleChange', 'close', 'deleteRule']);

const { $t } = useCommonImports();
const { getOperators } = useTableConditionalFormatter();
const form = ref(null);
const default_form_data = {
  field: props.fields[0],
  formatting_style: 'single_color',
  operator: 'equal',
  value: '',
  color: BI_DEFAULT_PALETTE_COLORS[0],
  apply_to: 'column',
  color_range: 5,
};

const field_details = computed(() => props.fieldsMap[form.value.field] || {});
const is_numeric = computed(() => ['integer', 'numeric', 'float'].includes(field_details.value.field_type));

function getFieldTitle() {
  if (props.chartType === 'map')
    return $t('Property');
  return $t('Column');
}

function getOperatorTitle() {
  if (props.chartType === 'map')
    return $t('Value');
  return $t('When a cell in this column');
}

function updateOperator() {
  if (getOperators(field_details.value).find(op => op.value === form.value.operator))
    return;
  form.value.operator = 'equal';
}

function onSave() {
  emits('ruleChange', {
    uid: props.ruleDetails?.uid || crypto.randomUUID(),
    ...form.value,
  });
}

function init() {
  form.value = {
    ...default_form_data,
    ...(props.ruleDetails || {}),
  };
}
init();
</script>

<template>
  <div>
    <Vueform
      v-model="form"
      size="sm"
      sync
      :messages="{
        required: $t('This field is required'),
        numeric: $t('This field must be numeric'),
      }"
      :display-errors="true"
      :endpoint="onSave"
    >
      <div class="col-span-12">
        <div
          class="flex items-center gap-2 cursor-pointer hover:underline text-sm font-semibold text-gray-700 mb-4"
          @click="emits('close')"
        >
          <IconHawkArrowLeft />
          {{ $t('Go back') }}
        </div>
        <div class="mb-4">
          <div class="text-sm font-semibold text-gray-900 mb-2">
            {{ getFieldTitle() }}
          </div>
          <SelectElement
            name="field"
            :items="props.fields"
            :default="props.fields[0]"
            :native="false"
            :can-clear="false"
            :can-deselect="false"
            placeholder="Select"
            @select="updateOperator()"
          />
        </div>

        <div v-show="is_numeric && !showIcon" class="mb-4">
          <div class="text-sm font-semibold text-gray-900 mb-2">
            {{ $t('Formatting style') }}
          </div>
          <RadiogroupElement
            name="formatting_style"
            :items="[
              { value: 'single_color', label: $t('Single color') },
              { value: 'color_range', label: $t('Color range') },
            ]"
            default="single_color"
          />
        </div>
        <div v-show="form?.formatting_style === 'single_color'" class="mb-6">
          <div class="text-sm font-semibold text-gray-900 mb-2">
            {{ getOperatorTitle() }}
          </div>
          <SelectElement
            name="operator"
            :items="getOperators(field_details)"
            class="mb-2"
            default="equal"
            :native="false"
            :can-clear="false"
            :can-deselect="false"
            placeholder="Select"
          />
          <template v-if="form?.formatting_style === 'single_color'">
            <TextElement
              v-if="is_numeric"
              name="value"
              :conditions="[
                ['operator', '!=', ['empty', 'not_empty']],
              ]"
              placeholder="0"
              :rules="['required', 'numeric']"
            />
            <TextElement
              v-else
              name="value"
              :conditions="[
                ['operator', '!=', ['empty', 'not_empty']],
              ]"
              :rules="['required']"
              placeholder="Enter value"
            />
          </template>
          <div v-if="showIcon" class="flex items-center justify-between my-2">
            <div class="text-sm font-semibold text-gray-900 mb-2">
              {{ $t('turn its icon to') }}
            </div>
            <BiIconPicker
              name="icon"
              type="outlined"
              :active-icon="form.icon || 'asset'"
              @icon-selected="form.icon = $event"
            />
          </div>
          <div v-else class="flex items-center justify-between py-2 mb-2">
            <div class="text-sm font-semibold text-gray-900">
              {{ $t('turn its background color to') }}
            </div>
            <BiColorPicker
              type="outlined"
              :active-color="form.color"
              @color-selected="form.color = $event"
            />
          </div>
        </div>

        <div v-show="form?.formatting_style === 'color_range'" class="mb-4">
          <BiColorRangePicker
            :form="form"
            @update-color-range="form.color_range = $event"
            @update-color="form.color = $event"
          />
        </div>

        <div v-if="['table', 'pivot_table'].includes(chartType)">
          <div class="text-sm font-semibold mb-2">
            {{ $t('Apply color to') }}
          </div>
          <SelectElement
            name="apply_to"
            :items="[
              { value: 'column', label: $t('This column') },
              { value: 'entire_row', label: $t('Entire row') },
            ]"
            default="column"
            :native="false"
            :can-clear="false"
            :can-deselect="false"
          />
        </div>

        <!-- Custom fields -->
        <TextElement
          v-show="showIcon"
          name="icon"
          class="hidden opacity-0"
          default="asset"
        />
        <HiddenElement name="color_range" :default="5" />
        <HiddenElement name="color" default="" />

        <div class="border-b border-gray-200 mb-6" />

        <div class="flex justify-between items-center mt-4" :class="{ '!justify-end': !ruleDetails.uid }">
          <HawkButton
            v-if="ruleDetails.uid"
            color="error" type="plain"
            @click="emits('deleteRule', ruleDetails.uid)"
          >
            {{ $t('Remove') }}
          </HawkButton>
          <ButtonElement
            name="save"
            :button-label="$t('Save')"
            :submits="true"
          />
        </div>
      </div>
    </Vueform>
  </div>
</template>

<style scoped lang="scss">

</style>
